import {
  Pill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components';
import type { WorkspaceSummaryResponse } from '@/shared/api';
import type { AdminInvitationResend } from './useAdminInvitationResend';
import { WorkspaceAdmins } from './WorkspaceAdmins';
import { WorkspaceRevisionHolds } from './WorkspaceRevisionHolds';
import { WorkspaceRowActions } from './WorkspaceRowActions';

interface Props {
  workspaces: WorkspaceSummaryResponse[];
  busyId: string | null;
  onToggleActive: (workspace: WorkspaceSummaryResponse) => void;
  onSupport: (workspace: WorkspaceSummaryResponse) => void;
  supportPending: boolean;
  invitation: AdminInvitationResend;
}

const kindLabel = (kind: string) => (kind === 'Platform' ? 'Plattform' : 'Kunde');

/**
 * Die Workspace-Liste der Plattformverwaltung — Tabelle, darunter gestapelte Karte.
 *
 * <p>„Keiner aktiv" bleibt die auffälligste Zeile der Seite: ein Workspace ohne aktiven
 * Administrator ist nicht mehr selbst bedienbar, und genau dafür gibt es diese Übersicht.</p>
 */
export function WorkspacesList({
  workspaces,
  busyId,
  onToggleActive,
  onSupport,
  supportPending,
  invitation,
}: Props) {
  return (
    <div className="@container">
      <div className="@3xl:block hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Workspace</TableHead>
              <TableHead>Art</TableHead>
              <TableHead>Administratoren</TableHead>
              <TableHead>Zustand</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Aktion</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workspaces.map((ws) => (
              <TableRow key={ws.id}>
                <TableCell className="align-top">
                  <span className="text-ink wrap-break-word font-serif font-medium">{ws.name}</span>
                  <span className="text-ink-faint mt-0.5 block font-mono text-xs">{ws.slug}</span>
                </TableCell>
                <TableCell className="text-ink-soft align-top text-sm">
                  {kindLabel(ws.kind)}
                </TableCell>
                <TableCell className="align-top">
                  <WorkspaceAdmins workspace={ws} invitation={invitation} />
                </TableCell>
                <TableCell className="align-top">
                  <Pill variant={ws.isActive ? 'live' : 'outline'} size="sm">
                    {ws.isActive ? 'Aktiv' : 'Gesperrt'}
                  </Pill>
                  <WorkspaceRevisionHolds workspace={ws} className="mt-1.5 max-w-40" />
                </TableCell>
                <TableCell className="text-right align-top">
                  <WorkspaceRowActions
                    workspace={ws}
                    busy={busyId === ws.id}
                    onToggle={onToggleActive}
                    onSupport={onSupport}
                    supportPending={supportPending}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="@3xl:hidden grid gap-3">
        {workspaces.map((ws) => (
          <li key={ws.id} className="border-line bg-card shadow-card rounded-lg border p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-ink wrap-break-word font-serif text-lg">{ws.name}</p>
                <p className="text-ink-faint wrap-break-word mt-0.5 font-mono text-xs">{ws.slug}</p>
              </div>
              <Pill variant={ws.isActive ? 'live' : 'outline'} size="sm">
                {ws.isActive ? 'Aktiv' : 'Gesperrt'}
              </Pill>
            </div>
            <WorkspaceRevisionHolds workspace={ws} className="mt-2" />
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
              <div className="min-w-0">
                <dt className="text-ink-faint tracking-wide">Art</dt>
                <dd className="text-ink mt-0.5">{kindLabel(ws.kind)}</dd>
              </div>
              <div className="col-span-2 min-w-0">
                <dt className="text-ink-faint tracking-wide">Administratoren</dt>
                <dd className="mt-1">
                  <WorkspaceAdmins workspace={ws} invitation={invitation} />
                </dd>
              </div>
            </dl>
            <div className="mt-3">
              <WorkspaceRowActions
                workspace={ws}
                busy={busyId === ws.id}
                onToggle={onToggleActive}
                onSupport={onSupport}
                supportPending={supportPending}
                fullWidth
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
